import React, { useCallback, useEffect, useState } from 'react';
import {
  downloadCsv,
  listWriters,
  updateWriter,
  writersCsvPath,
} from '../../../api/adminCrm';
import {
  ONBOARDING_STATUSES,
  WRITER_TYPES,
  formatWhen,
} from './format';
import './Crm.css';

function WritersPanel() {
  const [draftQuery, setDraftQuery] = useState('');
  const [draftSource, setDraftSource] = useState('');
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ writers: [], total: 0, page: 1, page_size: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listWriters({ q: query, source, status, page });
      setResult({
        writers: data?.writers || [],
        total: data?.total || 0,
        page: data?.page || page,
        page_size: data?.page_size || 50,
      });
    } catch (err) {
      setError(err.message || 'The writer list did not load.');
    } finally {
      setLoading(false);
    }
  }, [query, source, status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(result.total / result.page_size));

  const onSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
    setSource(draftSource.trim());
  };

  const changeFilter = (setter) => (event) => {
    setPage(1);
    setter(event.target.value);
  };

  const saveField = async (writer, patch) => {
    setSavingId(writer.id);
    setError('');
    try {
      const updated = await updateWriter(writer.id, patch);
      setResult((current) => ({
        ...current,
        writers: current.writers.map((row) => (
          row.id === writer.id ? { ...row, ...updated } : row
        )),
      }));
    } catch (err) {
      setError(err.message || 'That change did not save.');
    } finally {
      setSavingId(null);
    }
  };

  const onExport = async () => {
    setExporting(true);
    setError('');
    try {
      await downloadCsv(writersCsvPath({ q: query, source, status }), 'writers.csv');
    } catch (err) {
      setError(err.message || 'The CSV did not download.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="crm-panel" aria-labelledby="writers-heading">
      <div className="crm-panel__heading">
        <div>
          <p className="crm-eyebrow">Roster</p>
          <h2 id="writers-heading">Writers</h2>
          <p className="crm-panel__intro">
            People with an account. Search by name or email, then export the rows you are looking at.
            Each row shows how many invites they created and how many of those people signed up.
          </p>
        </div>
        <button type="button" className="crm-button" onClick={onExport} disabled={exporting}>
          {exporting ? 'Preparing CSV…' : 'Export CSV'}
        </button>
      </div>

      <form className="crm-toolbar" onSubmit={onSearch}>
        <div className="crm-field crm-field--grow">
          <label htmlFor="writer-search">Search name or email</label>
          <input
            id="writer-search"
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="crm-field">
          <label htmlFor="writer-source">Signup source</label>
          <input
            id="writer-source"
            value={draftSource}
            onChange={(event) => setDraftSource(event.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="crm-field">
          <label htmlFor="writer-status">Onboarding</label>
          <select id="writer-status" value={status} onChange={changeFilter(setStatus)}>
            <option value="">All</option>
            {ONBOARDING_STATUSES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <button type="submit" className="crm-button crm-button--primary">Search</button>
      </form>

      {error ? <p className="crm-error" role="alert">{error}</p> : null}
      {loading ? <p className="crm-status">Loading writers…</p> : null}

      {!loading && result.writers.length === 0 ? (
        <p className="crm-status">No writers match this search.</p>
      ) : null}

      {result.writers.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Name</th>
                <th scope="col">Joined</th>
                <th scope="col">Last seen</th>
                <th scope="col">Source</th>
                <th scope="col">Invites</th>
                <th scope="col">Signed up</th>
                <th scope="col">Writer type</th>
                <th scope="col">Onboarding</th>
              </tr>
            </thead>
            <tbody>
              {result.writers.map((writer) => (
                <tr key={writer.id}>
                  <td>{writer.email}</td>
                  <td>{writer.name || '—'}</td>
                  <td>{formatWhen(writer.created_at)}</td>
                  <td>{formatWhen(writer.last_seen)}</td>
                  <td>{writer.signup_source || '—'}</td>
                  <td>{writer.invite_count ?? 0}</td>
                  <td>{writer.invite_signups ?? 0}</td>
                  <td>
                    <label className="crm-sr" htmlFor={`type-${writer.id}`}>
                      Writer type for {writer.email}
                    </label>
                    <select
                      id={`type-${writer.id}`}
                      value={writer.writer_type || ''}
                      disabled={savingId === writer.id}
                      onChange={(event) => saveField(writer, {
                        writer_type: event.target.value || null,
                      })}
                    >
                      {WRITER_TYPES.map((option) => (
                        <option key={option.value || 'unset'} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <label className="crm-sr" htmlFor={`status-${writer.id}`}>
                      Onboarding for {writer.email}
                    </label>
                    <select
                      id={`status-${writer.id}`}
                      value={writer.onboarding_status || 'new'}
                      disabled={savingId === writer.id}
                      onChange={(event) => saveField(writer, {
                        onboarding_status: event.target.value,
                      })}
                    >
                      {ONBOARDING_STATUSES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="crm-pager">
        <button
          type="button"
          className="crm-button"
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          disabled={page <= 1 || loading}
        >
          Previous page
        </button>
        <p className="crm-status">Page {page} of {pageCount}</p>
        <button
          type="button"
          className="crm-button"
          onClick={() => setPage((current) => current + 1)}
          disabled={page >= pageCount || loading}
        >
          Next page
        </button>
      </div>
    </section>
  );
}

export default WritersPanel;
